import { ExpandMore } from '@mui/icons-material';
import { Accordion, AccordionDetails, Typography, useEventCallback } from '@mui/material';
import { PmiButton } from 'shell/components/shared/button';
import { FormikErrors, FormikTouched, useFormikContext } from 'formik';
import { PracticeDetailVM } from 'practice/view-models/practice-detail';
import { Fragment, memo, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AccordionSummary } from 'shell/components/shared/summary-accordion';
import { __CIVIC_API_URL__, __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { RequestCertificate } from 'shell/models/certification';
import { HttpResponseList } from 'shell/models/http-response';
import AccordionRequestAssessmentForm, { RequestAssessmentVM } from './form';
import { AxiosResponse } from 'axios';

interface RequestAssessmentProps {
  loadViewMessi: () => Promise<void>;
  loadUpdateDate: () => Promise<PracticeDetailVM> | void;
}

const AccordionRequestAssessmentComponent = ({
  loadViewMessi,
  loadUpdateDate,
}: RequestAssessmentProps): React.ReactElement => {
  const [open, setOpen] = useState(true);

  const ref = useRef<HTMLDivElement | null>(null);

  const params = useParams();

  const [list] = useHttpFetch<HttpResponseList<RequestCertificate>, Array<RequestCertificate>>(
    `${__CIVIC_API_URL__}/messi-civici/atti/lista-pratica`,
    {
      onBootstrap: true,
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {
          key: {
            codiceEnte: params.codiceEnte || undefined,
            tipoRegistro: params.tipoRegistro || undefined,
            idAnnoPratica: params.idAnnoPratica
              ? Number.parseInt(params.idAnnoPratica, 10)
              : undefined,
            progressivoPratica: params.progressivoPratica
              ? Number.parseInt(params.progressivoPratica, 10)
              : undefined,
          },
        },
      },
      transformer: (response: AxiosResponse<HttpResponseList<RequestCertificate>>) => {
        const { data } = response;
        return data?.payload?.list || [];
      },
    }
  );

  const updateData = useHttpClient(
    `${__PRACTICE_API_URL__}/accertamento-messi/richiesta/ulteriori-accertamenti`,
    {
      options: { method: 'POST' },
    }
  );

  useEffect(() => {
    ref.current?.scrollIntoView();
  }, []);

  const formContext = useFormikContext<PracticeDetailVM>();

  const onChange = useEventCallback(
    (key: keyof RequestAssessmentVM, value: RequestAssessmentVM[keyof RequestAssessmentVM]) => {
      formContext.setFieldValue('requestAssessment', {
        ...(formContext.values.requestAssessment || {}),
        [key]: value,
      });
    }
  );

  const onChanges = useEventCallback((values: RequestAssessmentVM) => {
    formContext.setFieldValue('requestAssessment', {
      ...values,
    });
  });

  const { isLoading } = list;

  const [hasAttentionNote, setHasAttentionNote] = useState<boolean>(false);

  useEffect(() => {
    setHasAttentionNote((oldState) => {
      if (oldState) {
        return oldState;
      }

      return ((list.data as Array<RequestCertificate>) || []).length > 0;
    });
  }, [list.data]);

  const onSave = useEventCallback(() => {
    const data = {
      codiceEnte: params.codiceEnte || undefined,
      tipoRegistro: params.tipoRegistro || undefined,
      idAnnoPratica: params.idAnnoPratica ? Number.parseInt(params.idAnnoPratica, 10) : undefined,
      progressivoPratica: params.progressivoPratica
        ? Number.parseInt(params.progressivoPratica, 10)
        : undefined,
      note: formContext.values.requestAssessment?.note || undefined,
      richiestaAccertamento: hasAttentionNote ? '1' : '0',
    };

    (updateData({ data }) as ResponsePromise<void>).then(async () => {
      toast.success('Operazione eseguita con successo');

      // aggiornamento stato pratica
      formContext.setFieldValue('practice', {
        ...(formContext.values.practice || {}),
        codiceStatoPratica: '5',
        tipoOperazione: '06',
      });

      // se cambia lo stato della pratica ricaricare dati
      const newData: PracticeDetailVM | void = await loadUpdateDate();

      if (newData) {
        formContext.setValues({
          ...(formContext.values || {}),
          ...newData,
        });
      }

      // ricaricare stato messi
      await loadViewMessi();
    });
  });

  if (isLoading) {
    return <></>;
  }

  return (
    <Fragment>
      <Accordion
        expanded={open}
        onChange={() => {
          setOpen(!open);
        }}
        ref={ref}
      >
        <AccordionSummary
          aria-controls="panel-request-assessment-content"
          id="panel-request-assessment-header"
          expandIcon={<ExpandMore />}
        >
          <Typography>Richiesta ulteriori accertamenti</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <AccordionRequestAssessmentForm
            values={formContext.values.requestAssessment}
            onChange={onChange}
            onChanges={onChanges}
            errors={formContext.errors.requestAssessment as FormikErrors<RequestAssessmentVM>}
            touched={
              formContext.touched.requestAssessment as unknown as FormikTouched<RequestAssessmentVM>
            }
            hasAttentionNote={hasAttentionNote}
            disabledMessi
          />
          <div className="pmi-align-right pmi-pt-2">
            <PmiButton
              color="cdmBrandThird"
              variant="outlined"
              onClick={() => onSave()}
              id="return"
              disabled={hasAttentionNote}
            >
              Conferma
            </PmiButton>
          </div>
        </AccordionDetails>
      </Accordion>
    </Fragment>
  );
};

AccordionRequestAssessmentComponent.displayName = 'Accordion request assessment component';

const AccordionRequestAssessment = memo(AccordionRequestAssessmentComponent);

export default AccordionRequestAssessment;
